import type { WidgetConfig, FeedResponse, FeedItem, ItemResponse, LayoutParams } from './types.js';
import { computeLayout } from './layout.js';

/**
 * Clear all children from the shadow root except <style> elements.
 */
function clearContent(root: ShadowRoot): void {
  const children = Array.from(root.children);
  for (const child of children) {
    if (child.tagName !== 'STYLE') {
      root.removeChild(child);
    }
  }
}

/**
 * Create the outer widget container and inner layout container,
 * setting CSS custom properties for columns and card image height.
 */
function createContainers(
  config: WidgetConfig,
  layout: LayoutParams,
): { widget: HTMLDivElement; container: HTMLDivElement } {
  const widget = document.createElement('div');
  widget.className = 'rss-widget';

  const container = document.createElement('div');
  container.className = layout.layout === 'slider' ? 'rss-slider' : 'rss-grid';

  widget.style.setProperty('--rss-columns', String(layout.columns));
  widget.style.setProperty('--rss-card-image-height', `${config.cardImageHeight}px`);

  widget.appendChild(container);
  return { widget, container };
}

/**
 * Render skeleton loading state with shimmer placeholder cards.
 */
export function renderSkeleton(root: ShadowRoot, config: WidgetConfig): void {
  clearContent(root);

  const hostWidth = (root.host as HTMLElement).offsetWidth || 800;
  const layout = computeLayout(config, hostWidth);
  const { widget, container } = createContainers(config, layout);

  const count = layout.layout === 'slider' ? layout.visibleItems : config.maxItems;

  for (let i = 0; i < count; i++) {
    const card = document.createElement('div');
    card.className = layout.layout === 'slider' ? 'rss-skeleton rss-card' : 'rss-skeleton';

    if (layout.layout === 'slider') {
      card.style.width = `${layout.cardWidth}px`;
    }

    // Skeleton image area
    const imgArea = document.createElement('div');
    imgArea.className = 'rss-card-image';
    card.appendChild(imgArea);

    // Skeleton body with lines
    const body = document.createElement('div');
    body.className = 'rss-card-body';

    for (let j = 0; j < 3; j++) {
      const line = document.createElement('div');
      line.className = 'rss-skeleton-line';
      body.appendChild(line);
    }

    card.appendChild(body);
    container.appendChild(card);
  }

  root.appendChild(widget);
}

/**
 * Render feed data as article cards in grid or slider layout.
 */
export function renderFeed(
  root: ShadowRoot,
  feed: FeedResponse,
  config: WidgetConfig,
  onCardClick: (item: FeedItem) => void,
): void {
  clearContent(root);

  const hostWidth = (root.host as HTMLElement).offsetWidth || 800;
  const layout = computeLayout(config, hostWidth);
  const { widget, container } = createContainers(config, layout);

  for (const item of feed.items) {
    const card = document.createElement('div');
    card.className = 'rss-card';
    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'article');

    if (layout.layout === 'slider') {
      card.style.width = `${layout.cardWidth}px`;
    }

    // Thumbnail
    if (item.thumbnail) {
      const imgContainer = document.createElement('div');
      imgContainer.className = 'rss-card-image';

      const img = document.createElement('img');
      img.src = item.thumbnail;
      img.alt = item.title;
      img.loading = 'lazy';

      imgContainer.appendChild(img);
      card.appendChild(imgContainer);
    }

    // Card body
    const body = document.createElement('div');
    body.className = 'rss-card-body';

    const title = document.createElement('div');
    title.className = 'rss-card-title';
    title.textContent = item.title;
    body.appendChild(title);

    // Meta section
    const meta = document.createElement('div');
    meta.className = 'rss-card-meta';

    if (config.showDate && item.publishedAt) {
      const date = document.createElement('span');
      date.textContent = new Date(item.publishedAt).toLocaleDateString();
      meta.appendChild(date);
    }

    if (config.showReadingTime && item.readingTimeMinutes) {
      const time = document.createElement('span');
      time.textContent = `${item.readingTimeMinutes} min read`;
      meta.appendChild(time);
    }

    if (meta.childElementCount > 0) {
      body.appendChild(meta);
    }

    card.appendChild(body);

    // Click handler
    card.addEventListener('click', () => onCardClick(item));
    card.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onCardClick(item);
      }
    });

    container.appendChild(card);
  }

  root.appendChild(widget);
}

/**
 * Render empty error state (silent failure — no visible error to user).
 */
export function renderError(root: ShadowRoot): void {
  clearContent(root);

  const errorDiv = document.createElement('div');
  errorDiv.className = 'rss-error';
  root.appendChild(errorDiv);
}

/**
 * Build the shared overlay + modal shell used by both skeleton and full render.
 * Returns references to the overlay, modal, and content container.
 */
function createModalShell(
  root: ShadowRoot,
  onClose: () => void,
): { overlay: HTMLDivElement; modal: HTMLDivElement; contentArea: HTMLDivElement; keydownHandler: (e: KeyboardEvent) => void } {
  const overlay = document.createElement('div');
  overlay.className = 'rss-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'rss-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.className = 'rss-modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '\u00d7';
  closeBtn.addEventListener('click', onClose);
  modal.appendChild(closeBtn);

  // Content area
  const contentArea = document.createElement('div');
  contentArea.className = 'rss-modal-content';
  modal.appendChild(contentArea);

  overlay.appendChild(modal);

  // Close on overlay background click (not modal itself)
  overlay.addEventListener('click', (e: MouseEvent) => {
    if (e.target === overlay) onClose();
  });

  // Close on Escape key
  const keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };
  document.addEventListener('keydown', keydownHandler);

  // Store handler reference for cleanup
  (overlay as any).__rssKeydownHandler = keydownHandler;

  root.appendChild(overlay);
  return { overlay, modal, contentArea, keydownHandler };
}

/**
 * Render a modal skeleton loader (shown while item content is being fetched).
 */
export function renderModalSkeleton(root: ShadowRoot, onClose: () => void): void {
  const { contentArea } = createModalShell(root, onClose);

  // Skeleton lines in content area
  for (let i = 0; i < 6; i++) {
    const line = document.createElement('div');
    line.className = 'rss-skeleton-line';
    if (i === 5) line.style.width = '40%';
    contentArea.appendChild(line);
  }
}

/**
 * Render the full article modal with item content.
 * Replaces any existing modal overlay in the shadow root.
 */
export function renderModal(
  root: ShadowRoot,
  item: ItemResponse,
  onClose: () => void,
  onShare: () => void,
): void {
  // Remove existing overlay if present (e.g. skeleton)
  closeModal(root);

  const { modal, contentArea } = createModalShell(root, onClose);

  // Build header (insert before content area)
  const header = document.createElement('div');
  header.className = 'rss-modal-header';

  const title = document.createElement('h2');
  title.textContent = item.title;
  header.appendChild(title);

  // Meta row: author, date, reading time
  const meta = document.createElement('div');
  meta.className = 'rss-card-meta';

  if (item.author) {
    const author = document.createElement('span');
    author.textContent = item.author;
    meta.appendChild(author);
  }

  if (item.publishedAt) {
    const date = document.createElement('span');
    date.textContent = new Date(item.publishedAt).toLocaleDateString();
    meta.appendChild(date);
  }

  if (item.readingTimeMinutes) {
    const time = document.createElement('span');
    time.textContent = `${item.readingTimeMinutes} min read`;
    meta.appendChild(time);
  }

  if (meta.childElementCount > 0) {
    header.appendChild(meta);
  }

  // Share button
  const shareBtn = document.createElement('button');
  shareBtn.className = 'rss-share-btn';
  shareBtn.textContent = 'Share';
  shareBtn.setAttribute('aria-label', 'Share article');
  shareBtn.addEventListener('click', onShare);
  header.appendChild(shareBtn);

  modal.insertBefore(header, contentArea);

  // Content
  if (item.contentType === 'full') {
    contentArea.innerHTML = item.content;
  } else {
    // Excerpt + "Read on source" link
    const excerpt = document.createElement('p');
    excerpt.textContent = item.content;
    contentArea.appendChild(excerpt);

    const link = document.createElement('a');
    link.href = item.link;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Read on source';
    link.className = 'rss-read-source';
    contentArea.appendChild(link);
  }
}

/**
 * Close and remove the modal overlay from the shadow root.
 * Also removes the document keydown listener.
 */
export function closeModal(root: ShadowRoot): void {
  const overlay = root.querySelector('.rss-modal-overlay');
  if (!overlay) return;

  const handler = (overlay as any).__rssKeydownHandler as ((e: KeyboardEvent) => void) | undefined;
  if (handler) {
    document.removeEventListener('keydown', handler);
  }

  overlay.remove();
}
